/**
 * Analysis apply / preview hooks (M8-B cross-cutting). The M5 NL layer and the
 * share/embed path consume these, mirroring M4's declarative posture:
 *
 *   • applyAnalysisSpec(mapSpec, analysis) — the ONE hook that seats an
 *     `analysis` block onto a MapSpec (an empty block collapses to `undefined`
 *     so a no-op analysis never bloats the persisted spec). Both the UI and an
 *     NL-emitted intent land through this, so there is one declarative result.
 *   • highlightTerritory(analysis, id) — the member row ids to light up via the
 *     existing M5 highlight overlay (no spec mutation).
 *   • previewOverlay(input) — a TRANSIENT overlay (rings) for a gesture in
 *     progress. It reads nothing off the saved spec and returns only geometry,
 *     so a preview can never mutate what is persisted.
 */

import { circleRing, type Ring } from "./geometry";
import type { AnalysisSpec, RadiusCenter } from "./spec";
import type { MapSpec } from "@/lib/map/mapspec";
import type { PointSet } from "@/lib/map/pointset";
import { resolveCenter } from "./radius";

/** True when an analysis block carries no artifacts (every sublayer empty). */
export function isEmptyAnalysis(a: AnalysisSpec | undefined): boolean {
  if (!a) return true;
  return (
    !a.territories?.items.length &&
    !a.radii?.length &&
    !a.driveTime?.bands.length &&
    !a.routes?.stopRowIds.length
  );
}

/**
 * Seat an `analysis` block onto a MapSpec. An empty block collapses to
 * `undefined` (a MapSpec with no analysis renders byte-identically to before
 * the block existed — the M8-A migration guarantee). Pure: returns a new spec.
 */
export function applyAnalysisSpec(mapSpec: MapSpec, analysis: AnalysisSpec | undefined): MapSpec {
  return { ...mapSpec, analysis: isEmptyAnalysis(analysis) ? undefined : analysis };
}

/** Member row ids of territory `id` (for the highlight overlay); [] if absent. */
export function highlightTerritory(analysis: AnalysisSpec | undefined, id: string): string[] {
  const t = analysis?.territories?.items.find((x) => x.id === id);
  return t ? [...t.memberRowIds] : [];
}

/** A transient, non-persisted overlay: rings to draw as a live preview. */
export interface PreviewOverlay {
  rings: Ring[];
}

/** Input describing a gesture in progress to preview (never persisted). */
export type PreviewInput =
  | { kind: "lasso"; ring: Ring }
  | { kind: "radius"; center: RadiusCenter; radiusMeters: number }
  | { kind: "rings"; rings: Ring[] };

/**
 * Build a transient overlay for a gesture in progress. Takes the PointSet only
 * to resolve a radius center that references a row; it NEVER reads or writes the
 * saved analysis block, so a preview cannot leak into persisted state.
 */
export function previewOverlay(input: PreviewInput, pointSet?: PointSet): PreviewOverlay {
  if (input.kind === "lasso") return { rings: input.ring.length >= 3 ? [input.ring] : [] };
  if (input.kind === "rings") return { rings: input.rings };
  const center = pointSet
    ? resolveCenter(input.center, pointSet)
    : Array.isArray(input.center)
      ? input.center
      : null;
  if (!center || input.radiusMeters <= 0) return { rings: [] };
  return { rings: [circleRing(center, input.radiusMeters)] };
}
